/** Same as the private helper in ts-common's `utils/object.ts`; swap to that once ts-common exports it. */
export const isPlainObject = (value: unknown): value is Record<string, unknown> => {
  if (typeof value !== 'object' || value === null) return false;
  const prototype = Object.getPrototypeOf(value);
  return prototype === Object.prototype || prototype === null;
};

type DistributiveOmit<T, K extends PropertyKey> = T extends unknown ? Omit<T, K> : never;

export const omit = <T extends object, K extends PropertyKey>(value: T, ...keys: K[]): DistributiveOmit<T, K> => {
  const copy = { ...value } as Record<PropertyKey, unknown>;
  for (const key of keys) delete copy[key];
  return copy as DistributiveOmit<T, K>;
};

/** Trims every string, including object keys. */
export const trimStringsDeep = (value: unknown): unknown => {
  if (typeof value === 'string') return value.trim();
  if (Array.isArray(value)) return value.map(trimStringsDeep);
  if (isPlainObject(value)) return Object.fromEntries(Object.entries(value).map(([key, item]) => [key.trim(), trimStringsDeep(item)]));
  return value;
};

const sortKeysDeep = (value: unknown): unknown => {
  if (Array.isArray(value)) return value.map(sortKeysDeep);
  if (!isPlainObject(value)) return value;
  return Object.fromEntries(
    Object.keys(value)
      .sort()
      .map((key) => [key, sortKeysDeep(value[key])]),
  );
};

/** JSON with every object's keys sorted, so equal content stringifies the same whatever the key order. Mirrors ts-common's. */
export const stableStringify = (value: unknown): string => JSON.stringify(sortKeysDeep(value)) ?? 'undefined';

interface LeafChange {
  path: (string | number)[];
  before: unknown;
  after: unknown;
}

/** Every value that differs between two documents, walking into plain objects; arrays and primitives compare whole. */
export const diffLeaves = (before: unknown, after: unknown, path: (string | number)[] = []): LeafChange[] => {
  if (stableStringify(before) === stableStringify(after)) return [];
  if (isPlainObject(before) && isPlainObject(after)) {
    return [...new Set([...Object.keys(before), ...Object.keys(after)])].sort().flatMap((key) => diffLeaves(before[key], after[key], [...path, key]));
  }
  return [{ path, before, after }];
};
